import { getAllOfficeSeoComparisonSlugs } from './officeSeoComparisonData';
import { OFFICE_SEO_HUB_DATA } from './officeSeoHubData';
import { nycCityOfficeSeo } from './cities/nycCityOfficeSeo';
import { sfCityOfficeSeo } from './cities/sfCityOfficeSeo';
import { nycFlatironNomadOfficeSeo } from './neighborhoods/nycFlatironNomad';
import { nycHudsonYardsOfficeSeo } from './neighborhoods/nycHudsonYards';
import { nycLowerManhattanOfficeSeo } from './neighborhoods/nycLowerManhattan';
import { nycMidtownOfficeSeo } from './neighborhoods/nycMidtown';
import { nycSohoTribecaOfficeSeo } from './neighborhoods/nycSohoTribeca';
import { nycUnionSquareOfficeSeo } from './neighborhoods/nycUnionSquare';
import { sfFinancialDistrictOfficeSeo } from './neighborhoods/sfFinancialDistrict';
import { sfHayesValleyOfficeSeo } from './neighborhoods/sfHayesValley';
import { sfMissionBayOfficeSeo } from './neighborhoods/sfMissionBay';
import { sfPotreroHillOfficeSeo } from './neighborhoods/sfPotreroHill';
import { sfSomaOfficeSeo } from './neighborhoods/sfSoma';

import type { NeighborhoodOfficeSeoData, OfficeSeoServiceKey } from './types';

/** Order for human-readable /sitemap — Office services — San Francisco */
export const OFFICE_SEO_SITEMAP_SF_NEIGHBORHOODS: NeighborhoodOfficeSeoData[] = [
  sfSomaOfficeSeo,
  sfFinancialDistrictOfficeSeo,
  sfHayesValleyOfficeSeo,
  sfMissionBayOfficeSeo,
  sfPotreroHillOfficeSeo
];

/** Order for human-readable /sitemap — Office services — New York */
export const OFFICE_SEO_SITEMAP_NYC_NEIGHBORHOODS: NeighborhoodOfficeSeoData[] = [
  nycFlatironNomadOfficeSeo,
  nycLowerManhattanOfficeSeo,
  nycMidtownOfficeSeo,
  nycSohoTribecaOfficeSeo,
  nycUnionSquareOfficeSeo,
  nycHudsonYardsOfficeSeo
];

export const OFFICE_SEO_NEIGHBORHOODS_ALL: NeighborhoodOfficeSeoData[] = [
  ...OFFICE_SEO_SITEMAP_SF_NEIGHBORHOODS,
  ...OFFICE_SEO_SITEMAP_NYC_NEIGHBORHOODS
];

const OFFICE_SEO_REGISTRY_KEY_SEPARATOR = ':';

function registryKey(cityPathSegment: string, neighborhoodSlug: string): string {
  return `${cityPathSegment}${OFFICE_SEO_REGISTRY_KEY_SEPARATOR}${neighborhoodSlug}`;
}

const officeSeoRegistry = new Map<string, NeighborhoodOfficeSeoData>();

for (const n of OFFICE_SEO_NEIGHBORHOODS_ALL) {
  officeSeoRegistry.set(registryKey(n.cityPathSegment, n.neighborhoodSlug), n);
}

export function getNeighborhoodOfficeSeo(
  cityPathSegment: string,
  neighborhoodSlug: string
): NeighborhoodOfficeSeoData | null {
  return officeSeoRegistry.get(registryKey(cityPathSegment, neighborhoodSlug)) ?? null;
}

/** URL path segment for each service (matches ENGINEERING_REQUIREMENTS.md) */
export const OFFICE_SEO_SERVICE_PATH_SLUG: Record<OfficeSeoServiceKey, string> = {
  cleaning: 'cleaning',
  pantryRestocking: 'pantry-restocking',
  internet: 'internet',
  officeAssistant: 'office-assistant',
  officeFurniture: 'office-furniture',
  deepCleaning: 'deep-cleaning',
  carpetCleaning: 'carpet-cleaning',
  windowCleaning: 'window-cleaning',
  glassConferenceRoom: 'glass-conference-room-cleaning',
  wasteRecycling: 'waste-recycling',
  pantryInventory: 'pantry-inventory',
  coffeeService: 'coffee-service',
  waterService: 'water-service',
  teamLunch: 'team-lunch',
  breakfastCatering: 'breakfast-catering',
  individualMeals: 'individual-meals',
  adHocCatering: 'ad-hoc-catering',
  happyHour: 'happy-hour',
  conferenceRoomAv: 'conference-room-av',
  networkInstallation: 'network-installation',
  networkManagement: 'network-management',
  cableManagement: 'cable-management',
  roomBooking: 'room-booking',
  printingSetup: 'printing-setup',
  interiorDesign: 'interior-design',
  officeMurals: 'office-murals',
  officePainting: 'office-painting',
  glassFrosting: 'glass-frosting',
  officeSignage: 'office-signage',
  careRoom: 'care-room',
  furnitureAssembly: 'furniture-assembly',
  layoutChange: 'layout-change',
  officePlants: 'office-plants',
  keylessAccess: 'keyless-access',
  alarmSystems: 'alarm-systems',
  securityCameras: 'security-cameras',
  keypads: 'keypads',
  lockboxes: 'lockboxes',
  visitorManagement: 'visitor-management',
  businessInsurance: 'business-insurance',
  electricalServices: 'electrical-services',
  hvac: 'hvac',
  plumbing: 'plumbing',
  pestControl: 'pest-control',
  officeMoving: 'office-moving',
  mountingInstallation: 'mounting-installation'
};

export const OFFICE_SEO_SERVICE_KEYS: OfficeSeoServiceKey[] = [
  'cleaning',
  'pantryRestocking',
  'internet',
  'officeAssistant',
  'officeFurniture',
  'deepCleaning',
  'carpetCleaning',
  'windowCleaning',
  'glassConferenceRoom',
  'wasteRecycling',
  'pantryInventory',
  'coffeeService',
  'waterService',
  'teamLunch',
  'breakfastCatering',
  'individualMeals',
  'adHocCatering',
  'happyHour',
  'conferenceRoomAv',
  'networkInstallation',
  'networkManagement',
  'cableManagement',
  'roomBooking',
  'printingSetup',
  'interiorDesign',
  'officeMurals',
  'officePainting',
  'glassFrosting',
  'officeSignage',
  'careRoom',
  'furnitureAssembly',
  'layoutChange',
  'officePlants',
  'keylessAccess',
  'alarmSystems',
  'securityCameras',
  'keypads',
  'lockboxes',
  'visitorManagement',
  'businessInsurance',
  'electricalServices',
  'hvac',
  'plumbing',
  'pestControl',
  'officeMoving',
  'mountingInstallation'
];

/**
 * The original 5 services shown in the footer.
 * New Phase 4a+ services are excluded from the footer to avoid clutter.
 */
export const OFFICE_SEO_FOOTER_SERVICE_KEYS: OfficeSeoServiceKey[] = [
  'cleaning',
  'pantryRestocking',
  'internet',
  'officeAssistant',
  'officeFurniture'
];

/** Display label used on /sitemap (prefix only; full label is "{label} in {nbhd}, {metro}") */
export const OFFICE_SEO_SITEMAP_SERVICE_LABEL: Record<OfficeSeoServiceKey, string> = {
  cleaning: 'Cleaning',
  pantryRestocking: 'Pantry restocking',
  internet: 'Internet & IT',
  officeAssistant: 'Office assistant',
  officeFurniture: 'Basic furniture',
  deepCleaning: 'Deep cleaning',
  carpetCleaning: 'Carpet & upholstery cleaning',
  windowCleaning: 'Window cleaning',
  glassConferenceRoom: 'Conference room glass cleaning',
  wasteRecycling: 'Waste & recycling hauling',
  pantryInventory: 'Pantry inventory & auto-ordering',
  coffeeService: 'Coffee service',
  waterService: 'Water service',
  teamLunch: 'Recurring team lunch',
  breakfastCatering: 'Recurring breakfast catering',
  individualMeals: 'Individual meal delivery',
  adHocCatering: 'Ad-hoc catering',
  happyHour: 'Happy hour',
  conferenceRoomAv: 'Conference room A/V setup',
  networkInstallation: 'Network installation',
  networkManagement: 'Managed network services',
  cableManagement: 'Cable management',
  roomBooking: 'Conference room booking systems',
  printingSetup: 'Printing setup',
  interiorDesign: 'Interior design & space planning',
  officeMurals: 'Office murals',
  officePainting: 'Office painting',
  glassFrosting: 'Glass frosting & privacy film',
  officeSignage: 'Office signage',
  careRoom: 'Care room setup',
  furnitureAssembly: 'Furniture assembly & repairs',
  layoutChange: 'Office layout changes',
  officePlants: 'Office plants & biophilic decor',
  keylessAccess: 'Keyless & digital access',
  alarmSystems: 'Alarm systems',
  securityCameras: 'Security cameras & doorbells',
  keypads: 'Door keypads',
  lockboxes: 'Lockboxes',
  visitorManagement: 'Visitor management systems',
  businessInsurance: 'Business insurance',
  electricalServices: 'Electrical services',
  hvac: 'HVAC maintenance',
  plumbing: 'Plumbing services',
  pestControl: 'Pest control',
  officeMoving: 'Office moving',
  mountingInstallation: 'Mounting & installation'
};

export function officeSeoPath(
  cityPathSegment: string,
  neighborhoodSlug: string,
  serviceKey: OfficeSeoServiceKey
): string {
  const seg = OFFICE_SEO_SERVICE_PATH_SLUG[serviceKey];
  return `/${cityPathSegment}/${neighborhoodSlug}/${seg}`;
}

export function getAllOfficeSeoIndexedPaths(): string[] {
  const paths: string[] = [];
  for (const n of OFFICE_SEO_NEIGHBORHOODS_ALL) {
    for (const key of OFFICE_SEO_SERVICE_KEYS) {
      paths.push(officeSeoPath(n.cityPathSegment, n.neighborhoodSlug, key));
    }
  }
  return paths;
}

export function parseOfficeSeoCityFromPathname(pathname: string): 'san-francisco' | 'new-york' | null {
  if (pathname.startsWith('/new-york')) return 'new-york';
  if (pathname.startsWith('/san-francisco')) return 'san-francisco';
  return null;
}

// ─── City-level service pages (/services/:serviceSlug-:citySlug) ─────────────

/** URL service slug used in /services/:slug city-level pages */
export const OFFICE_SEO_CITY_SERVICE_SLUG: Record<OfficeSeoServiceKey, string> = {
  cleaning: 'office-cleaning',
  pantryRestocking: 'office-pantry-management',
  internet: 'office-internet',
  officeAssistant: 'office-assistant',
  officeFurniture: 'office-furniture',
  deepCleaning: 'office-deep-cleaning',
  carpetCleaning: 'office-carpet-cleaning',
  windowCleaning: 'office-window-cleaning',
  glassConferenceRoom: 'conference-room-glass-cleaning',
  wasteRecycling: 'office-waste-recycling',
  pantryInventory: 'pantry-inventory-auto-ordering',
  coffeeService: 'office-coffee-service',
  waterService: 'office-water-service',
  teamLunch: 'office-team-lunch',
  breakfastCatering: 'office-breakfast-catering',
  individualMeals: 'office-individual-meals',
  adHocCatering: 'office-catering',
  happyHour: 'office-happy-hour',
  conferenceRoomAv: 'office-conference-room-av',
  networkInstallation: 'office-network-installation',
  networkManagement: 'office-network-management',
  cableManagement: 'office-cable-management',
  roomBooking: 'office-room-booking',
  printingSetup: 'office-printing-setup',
  interiorDesign: 'office-interior-design',
  officeMurals: 'office-murals',
  officePainting: 'office-painting',
  glassFrosting: 'office-glass-frosting',
  officeSignage: 'office-signage',
  careRoom: 'office-care-room',
  furnitureAssembly: 'office-furniture-assembly',
  layoutChange: 'office-layout-change',
  officePlants: 'office-plants',
  keylessAccess: 'office-keyless-access',
  alarmSystems: 'office-alarm-systems',
  securityCameras: 'office-security-cameras',
  keypads: 'office-keypads',
  lockboxes: 'office-lockboxes',
  visitorManagement: 'office-visitor-management',
  businessInsurance: 'office-business-insurance',
  electricalServices: 'office-electrical-services',
  hvac: 'office-hvac',
  plumbing: 'office-plumbing',
  pestControl: 'office-pest-control',
  officeMoving: 'office-moving',
  mountingInstallation: 'office-mounting-installation'
};

const OFFICE_SEO_CITY_URL_SLUG: Record<'san-francisco' | 'new-york', string> = {
  'san-francisco': 'san-francisco',
  'new-york': 'new-york-city'
};

/** Data object for each city's city-wide pages */
export const OFFICE_SEO_CITY_DATA: Record<'san-francisco' | 'new-york', NeighborhoodOfficeSeoData> = {
  'san-francisco': sfCityOfficeSeo,
  'new-york': nycCityOfficeSeo
};

/** Generates the /services/:slug path for a city-level service page */
export function officeSeoPathCity(
  cityPathSegment: 'san-francisco' | 'new-york',
  serviceKey: OfficeSeoServiceKey
): string {
  const serviceSlug = OFFICE_SEO_CITY_SERVICE_SLUG[serviceKey];
  const citySlug = OFFICE_SEO_CITY_URL_SLUG[cityPathSegment];
  return `/services/${serviceSlug}-${citySlug}`;
}

/**
 * Parses a /services/:slug param into city + serviceKey.
 * Returns null if the slug does not match any known city-level service page.
 */
export function parseCityServiceFromSlug(
  slug: string
): { cityPathSegment: 'san-francisco' | 'new-york'; serviceKey: OfficeSeoServiceKey } | null {
  for (const [cityPathSegment, citySlug] of Object.entries(OFFICE_SEO_CITY_URL_SLUG) as [
    'san-francisco' | 'new-york',
    string
  ][]) {
    const suffix = `-${citySlug}`;
    if (slug.endsWith(suffix)) {
      const serviceSlug = slug.slice(0, -suffix.length);
      const serviceEntry = (Object.entries(OFFICE_SEO_CITY_SERVICE_SLUG) as [OfficeSeoServiceKey, string][]).find(
        ([, s]) => s === serviceSlug
      );
      if (serviceEntry) {
        return { cityPathSegment, serviceKey: serviceEntry[0] };
      }
    }
  }
  return null;
}

export function getAllOfficeSeoIndexedCityPaths(): string[] {
  return (['san-francisco', 'new-york'] as const).flatMap(city =>
    OFFICE_SEO_SERVICE_KEYS.map(serviceKey => officeSeoPathCity(city, serviceKey))
  );
}

// ─── Hub / landing pages (/office-management-:locationSlug) ──────────────────

export function officeSeoPathHub(locationSlug: string): string {
  return `/office-management-${locationSlug}`;
}

export function getAllOfficeSeoIndexedHubPaths(): string[] {
  return Object.keys(OFFICE_SEO_HUB_DATA).map(officeSeoPathHub);
}

// ─── Comparison / decision-stage pages (/vs/:slug) ───────────────────────────

export function officeSeoPathComparison(slug: string): string {
  return `/vs/${slug}`;
}

export function getAllOfficeSeoIndexedComparisonPaths(): string[] {
  return getAllOfficeSeoComparisonSlugs().map(officeSeoPathComparison);
}
